import { getCollection, getCollectionGroup } from './firestoreService';
import { getAnalyticsEvents } from './analyticsService';
import { getUserEngagementInsights } from './analyticsService';

export interface ContentQualityMetrics {
  topicTitle: string;
  qualityScore: number;
  completionRate: number;
  engagementTime: number;
  dropOffRate: number;
}

export interface AIGenerationMetrics {
  successRate: number;
  avgGenerationTime: number;
  avgModuleGenerationTime: number; // Average time per module (total generation time / number of modules)
  avgCostPerGeneration: number;
  revisionRate: number;
}

export interface ContentGap {
  searchedTopic: string;
  searchCount: number;
  hasContent: boolean;
}

export interface ContentPerformanceAnalytics {
  contentQuality: ContentQualityMetrics[];
  aiGenerationMetrics: AIGenerationMetrics;
  contentGaps: ContentGap[];
  lowPerformingContent: ContentQualityMetrics[];
}

export const getContentPerformanceAnalytics = async (
  days: number = 30
): Promise<ContentPerformanceAnalytics> => {
  try {
    const events = await getAnalyticsEvents(days);
    const engagementInsights = await getUserEngagementInsights();

    // Get content validation data if available
    const contentValidations = await getCollection('content_validations', undefined, undefined, undefined, 1000)
      .catch(() => []);

    // Get content generation events
    const generationEvents = events.filter((e) =>
      e.eventName === 'content_generation_requested' || e.eventName.includes('content_generation')
    );

    const successEvents = generationEvents.filter((e) => e.properties.success !== false);
    const successRate = generationEvents.length > 0
      ? (successEvents.length / generationEvents.length) * 100
      : 0;

    const avgGenerationTime = generationEvents.reduce((sum, e) => {
      const time = (e.properties.generation_time as number) || 0;
      return sum + time;
    }, 0) / (generationEvents.length || 1);

    // Calculate average module generation time from actual learning paths
    let avgModuleGenerationTime = 0;
    try {
      // Query adaptive_learning_paths collectionGroup to get all learning paths across all users
      // Filter only by generation_time_seconds > 0 to avoid composite index requirement
      // We'll filter by date in memory if needed
      const learningPaths = await getCollectionGroup('adaptive_learning_paths', [
        { field: 'generation_time_seconds', operator: '>', value: 0 }
      ], undefined, undefined, 10000).catch(() => []);

      if (learningPaths.length > 0) {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        
        let totalModuleTime = 0;
        let totalModules = 0;

        learningPaths.forEach((path: any) => {
          // Filter by date in memory to avoid composite index requirement
          const createdAt = path.created_at || path.generation_started_at;
          if (createdAt) {
            const pathDate = new Date(createdAt);
            if (pathDate < cutoffDate) {
              return; // Skip paths older than the specified days
            }
          }

          const generationTimeSeconds = path.generation_time_seconds || 0;
          const modules = path.modules || [];
          const moduleCount = Array.isArray(modules) ? modules.length : 0;

          if (generationTimeSeconds > 0 && moduleCount > 0) {
            // Calculate time per module for this topic
            const timePerModule = generationTimeSeconds / moduleCount;
            totalModuleTime += timePerModule;
            totalModules += 1; // Count topics, not individual modules
          }
        });

        if (totalModules > 0) {
          avgModuleGenerationTime = totalModuleTime / totalModules;
        }
      }
    } catch (error) {
      console.error('Error calculating average module generation time:', error);
      // Continue with 0 if calculation fails
    }

    // Content quality metrics (from engagement insights)
    const contentQuality: ContentQualityMetrics[] = engagementInsights.topProgressTopics.map((topic: any) => {
      const completionRate = topic.averageProgress || 0;
      return {
        topicTitle: topic.title,
        qualityScore: completionRate > 70 ? 5 : completionRate > 50 ? 4 : completionRate > 30 ? 3 : 2,
        completionRate,
        engagementTime: 0, // Would need time tracking
        dropOffRate: 0, // Would need drop-off tracking
      };
    });

    // Content gaps (searched topics without content)
    const searchEvents = events.filter((e) =>
      e.eventName === 'assistant_search_query' || e.eventName.includes('search')
    );
    const searchedTopics = new Map<string, number>();
    const availableTopics = new Set(engagementInsights.popularTopics.map((t: any) => t.title.toLowerCase()));

    searchEvents.forEach((event) => {
      const query = (event.properties.query as string) || '';
      const words = query.toLowerCase().split(/\s+/).filter((w) => w.length > 3);
      words.forEach((word) => {
        searchedTopics.set(word, (searchedTopics.get(word) || 0) + 1);
      });
    });

    const contentGaps: ContentGap[] = Array.from(searchedTopics.entries())
      .map(([topic, count]) => ({
        searchedTopic: topic,
        searchCount: count,
        hasContent: availableTopics.has(topic.toLowerCase()),
      }))
      .filter((gap) => !gap.hasContent)
      .sort((a, b) => b.searchCount - a.searchCount)
      .slice(0, 20);

    // Low performing content (completion rate < 30%)
    const lowPerformingContent = contentQuality
      .filter((content) => content.completionRate < 30)
      .sort((a, b) => a.completionRate - b.completionRate)
      .slice(0, 20);

    return {
      contentQuality: contentQuality.slice(0, 50),
      aiGenerationMetrics: {
        successRate,
        avgGenerationTime,
        avgModuleGenerationTime,
        avgCostPerGeneration: 0.002, // Estimated
        revisionRate: 0, // Would need revision tracking
      },
      contentGaps,
      lowPerformingContent,
    };
  } catch (error) {
    console.error('Error calculating content performance analytics:', error);
    throw error;
  }
};

